import { c as _c } from "react/compiler-runtime";
// 2026-08-23 button — radix slot, cva, focus-visible 2px, 2026 a11y
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import "./button.css";

/**
 * Button variants aligned with the local Vortixy design tokens:
 * - Primary: dark foreground bg, white text
 * - Accent: terracotta brand color (used sparingly for key CTAs)
 * - Hero: marketing gradient (Lovable-hero SSOT --hero-*, opt-in for hero CTAs only)
 * - Glass: translucent blurred chrome (overlays, floating bars)
 * - Ghost/Outline: transparent or bordered
 * - All use radius-button (9999px pill)
 * --accent = app chrome monocromo, --hero-* = marketing. default/accent intactos.
 * 2026-09-21 FASE 3 FIXER (research 2026 WCAG 2.2 2.4.13 + WebAIM: anillo 3:1):
 * foco base con --accent 2px+offset ≥3:1 en ambos temas; --hero-focus-ring
 * (sólido #a855f7: 4.72 dark / 3.70 light) SOLO opt-in en variant hero para
 * el ring — --hero-focus al 45% (1.7-1.9:1) queda solo en glows decorativos.
 */
const buttonVariants = cva(
// No text-ellipsis: long labels must stay readable on narrow screens.
// Layouts should stack/wrap buttons instead of clipping copy.
"inline-flex cursor-pointer items-center justify-center gap-2 text-sm font-medium transition-[color,background-color,opacity,transform,box-shadow,border-color] duration-[var(--duration-fast)] ease-[var(--ease-out-quart)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 max-w-full text-center text-balance sm:whitespace-nowrap sm:text-pretty", {
  variants: {
    variant: {
      // 2026-09-26 los text-color van en btn-text-* (button.css), NUNCA como
      // utilidad text-* en la variante: tailwind-merge los eliminaría frente
      // al text-[var(--step--1)] de size (verificado en Node). Sin excepción.
      default: "bg-foreground btn-text-background shadow-[0_1px_0_rgba(255,255,255,0.1)_inset,0_1px_2px_rgba(0,0,0,0.2)] hover:opacity-92 active:scale-[0.98]",
      accent: "bg-[var(--accent-deep)] btn-text-accent-fg font-semibold shadow-[0_1px_0_rgba(255,255,255,0.14)_inset,0_10px_28px_var(--accent-glow)] hover:opacity-90 active:scale-[0.98]",
      // 2026-09-11 Lovable-hero (opt-in, solo CTAs marketing): gradiente exacto
      // --hero-* con scrim negro 14% + text-shadow para elevar el contraste
      // efectivo del blanco (los extremos azul/rojo pasan 4.5:1, la zona
      // magenta de transición es decorativa). Nunca para body copy.
      // 2026-09-21 FASE 3 FIXER: focus-visible:ring hero-focus-ring SOLO aquí
      // (opt-in marketing, anillo 3:1 verificado); el base usa --accent.
      // 2026-09-22 FASE 3 FIX tokens P2: fondo vía .btn-hero-gradient SSOT
      // (hero-tokens.css, sin fallback congelado) — resto intacto.
      hero: "btn-hero-gradient border border-transparent btn-text-solid-white font-semibold [text-shadow:0_1px_8px_rgba(0,0,0,0.35)] shadow-[0_10px_28px_-8px_var(--hero-focus,rgba(168,85,247,0.45)),inset_0_1px_0_rgba(255,255,255,0.25)] hover:brightness-110 active:scale-[0.98] focus-visible:ring-[var(--hero-focus-ring,#a855f7)]",
      // 2026-09-11 glass monocromo (app chrome, no marketing): translúcido.
      // 2026-09-20 FASE 3 (research R2 2026 backdrop-filter: cada botón con blur es una capa
      // de repaint; sin uso real en tsx — el translúcido + borde basta, sin blur).
      glass: "border border-[var(--border-strong)] bg-[color-mix(in_srgb,var(--foreground)_8%,transparent)] btn-text-foreground hover:bg-[color-mix(in_srgb,var(--foreground)_13%,transparent)] active:scale-[0.98]",
      outline: "border border-[var(--border-strong)] bg-transparent shadow-[0_1px_0_rgba(255,255,255,0.03)_inset] hover:bg-[var(--muted)] hover:border-[var(--foreground-soft)]",
      ghost: "bg-transparent hover:bg-[var(--muted)] hover:text-foreground",
      // 2026-09-22 FASE 3 FIX tokens P3 (research 2026 single-source danger):
      // variante danger SSOT — blanco sobre #b91c1c 6.47:1, hover #991b1b
      // 8.31:1 (AA 4.5:1 ambos temas). Sin overrides inline en callers.
      // 2026-09-23 FASE 3 FIX diseño system-tokens (research 2026 single-source tokens:
      // hex literales #b91c1c/#991b1b → token --danger (light #b91c1c / dark #e8394a),
      // una sola fuente; hover por brightness, sin segundo hex).
      danger: "bg-[var(--danger)] btn-text-solid-white font-semibold hover:brightness-110 active:scale-[0.98]",
      muted: "bg-[var(--muted)] btn-text-muted-fg hover:bg-[var(--border)]",
      link: "bg-transparent btn-text-muted-fg underline-offset-4 hover:underline p-0 h-auto",
      // 2026-10-10 H6 — censo de botones ad-hoc del chat (73 ficheros .tsx,
      // 336 `<button>` crudos, 0 imports de `Button`): 202 repeticiones de
      // la línea de foco
      // `focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]`
      // y 52 pills `inline-flex min-h-8 … rounded-full … px-3 py-1 text-[11px]`.
      // La FORMA no puede vivir aquí: cva aplica `size` DESPUÉS de la
      // variante y tailwind-merge haría ganar `rounded-[var(--radius-button)]`
      // / `px-4` de `size` sobre `rounded-full` / `px-3` (verificado con el
      // twMerge del repo) — por eso las formas son los tamaños `chat-pill` /
      // `chat-icon` y aquí solo va el relleno. Color en `btn-text-*`
      // (button.css), nunca `text-*` en la variante: twMerge lo eliminaría
      // contra el `text-[var(--step--1)]` de `size` (mismo motivo que el
      // comentario de arriba de este bloque).
      "chat-accent": "bg-[var(--accent)] btn-text-accent-fg font-medium hover:opacity-90"
    },
    size: {
      // 2026-10-10 H6 — las dos formas del chat más repetidas (ver comentario
      // de la variante `chat-accent`): pill 11px e icono circular size-8.
      // `rounded-full`/`px-3` van aquí porque `size` se aplica el último.
      "chat-pill": "min-h-8 gap-1 rounded-full px-3 py-1 text-[11px]",
      "chat-icon": "size-8 shrink-0 rounded-full justify-center p-0",
      // 2026-09-25 RUN1 FASE3-FIX (SEED=2026-09-25-c6c873bf; research 2026
      // accessibility.build fluid-type https://accessibility.build/guides/accessible-typography-wcag:
      // 14/13px fijos → --step--1 fluido SSOT, 15px lg → --step-0; jerarquía intacta).
      default: "min-h-11 sm:h-10 h-auto rounded-[var(--radius-button)] px-4 py-2.5 text-[var(--step--1)] sm:px-5",
      sm: "min-h-11 sm:h-9 h-auto rounded-[var(--radius-button)] px-3 py-2 text-[var(--step--1)] sm:px-3.5 sm:py-1.5 sm:text-[var(--step--1)]",
      lg: "min-h-12 h-auto rounded-[var(--radius-button)] px-6 py-3 text-[var(--step-0)] tracking-[-0.01em] sm:px-7",
      icon: "sm:size-9 size-11 shrink-0 rounded-[var(--radius-button)] justify-center",
      "icon-sm": "sm:size-7 size-11 shrink-0 rounded-[var(--radius-sm)] justify-center"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
function Button(t0) {
  const $ = _c(26);
  let className;
  let loading;
  let props;
  let size;
  let t1;
  let type;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      asChild: t1,
      type,
      loading,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = loading;
    $[3] = props;
    $[4] = size;
    $[5] = t1;
    $[6] = type;
    $[7] = variant;
  } else {
    className = $[1];
    loading = $[2];
    props = $[3];
    size = $[4];
    t1 = $[5];
    type = $[6];
    variant = $[7];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = asChild ? Slot : "button";
  const t2 = asChild ? undefined : type ?? "button";
  let t3;
  if ($[8] !== className || $[9] !== loading || $[10] !== size || $[11] !== variant) {
    t3 = cn(buttonVariants({
      variant,
      size,
      className
    }), loading && "pointer-events-none opacity-70");
    $[8] = className;
    $[9] = loading;
    $[10] = size;
    $[11] = variant;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const t4 = props.disabled || loading;
  const t5 = loading || undefined;
  const t6 = props["aria-label"];
  let t7;
  if ($[13] !== asChild || $[14] !== loading || $[15] !== props.children) {
    t7 = loading && !asChild ? <><Loader2 className="size-4 animate-spin" aria-hidden="true" /><span>{props.children}</span></> : props.children;
    $[13] = asChild;
    $[14] = loading;
    $[15] = props.children;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== Comp || $[18] !== props || $[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7) {
    t8 = <Comp data-slot="button" type={t2} className={t3} disabled={t4} aria-busy={t5} aria-label={t6} {...props}>{t7}</Comp>;
    $[17] = Comp;
    $[18] = props;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
}
export { Button, buttonVariants };